import { abbreviateNumber } from '@gonfalon/format/abbreviateNumber';
import { formatNumber } from '@gonfalon/format/formatNumber';

import { pluralizeAbbreviatedName } from '../../../../utils';

type GetMarkLineOptions = {
  abbreviatedName: string;
  precision: number;
  limitValue?: number;
  avgUsage?: number;
  condenseLabels?: boolean;
  usageResets: number[];
  trialEndUTC?: number;
};

export function getMarkLines({
  abbreviatedName,
  precision,
  limitValue,
  avgUsage,
  usageResets,
  condenseLabels,
  trialEndUTC,
}: GetMarkLineOptions) {
  const data = [];

  if (limitValue) {
    data.push(getLimitMarkLine(abbreviatedName, limitValue, precision, condenseLabels));
  }

  if (avgUsage && avgUsage >= Math.pow(10, -precision)) {
    data.push(getAvgUsageMarkLine(abbreviatedName, avgUsage, precision, condenseLabels));
  }

  // Add monthly usage reset markers (xAxis)
  for (const ts of usageResets) {
    data.push({
      xAxis: ts,
      label: { formatter: 'Usage reset', show: true },
      lineStyle: { type: 'solid' as const },
    });
  }

  // Add trial end date marker (xAxis)
  if (trialEndUTC) {
    data.push({
      xAxis: trialEndUTC,
      label: { formatter: 'Trial end', show: true },
      lineStyle: { type: 'dashed' as const },
    });
  }

  return data.length ? { data } : undefined;
}

export function getLimitMarkLine(
  abbreviatedName: string,
  limitValue: number,
  precision: number,
  condenseLabels?: boolean,
) {
  const formattedLimit = formatNumber(limitValue, { maximumFractionDigits: precision });
  return {
    yAxis: limitValue,
    label: {
      show: true,
      color: 'var(--lp-color-data-negative)',
      formatter: condenseLabels
        ? 'Limit'
        : `Limit: ${formattedLimit} ${pluralizeAbbreviatedName(abbreviatedName, limitValue)}`,
    },
    lineStyle: {
      color: 'var(--lp-color-data-negative)',
    },
  };
}

function getAvgUsageMarkLine(abbreviatedName: string, avgUsage: number, precision: number, condenseLabels?: boolean) {
  const unit = condenseLabels ? '' : ` ${pluralizeAbbreviatedName(abbreviatedName, avgUsage)}`;
  return {
    yAxis: avgUsage,
    label: {
      show: true,
      formatter: `Avg: ${abbreviateNumber(avgUsage, { maximumFractionDigits: precision })}${unit}`,
    },
  };
}
